import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SupplierController from './controllers/supplier-controller';
import AddSupplier from './admin-components/AddSupplier';
import Modal from '../../components/Modal';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';
const SuppliersPage = () => {
  // State for the suppliers page
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOptions] = useState([5, 10, 20, 50]);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSupplierModalOpen, setIsEditSupplierModalOpen] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [isViewSupplierModalOpen, setIsViewSupplierModalOpen] = useState(false);
  const [viewedSupplier, setViewedSupplier] = useState(null);
  const {hasPermission} = usePermissionViewModel('fournisseur')


  // Fetch suppliers from API
  useEffect(() => {
    const fetchSuppliers = async () => {
      setLoading(true);
      try {
        const response = await SupplierController.fetchSuppliers(currentPage, pageSize);
        setSuppliers(response.list || []);
        setTotalItems(response.total || 0);
        setMaxPage(response.maxPage || 1);
        setError(null);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        setError('Une erreur est survenue lors du chargement des fournisseurs.');
        toast.error('Erreur de chargement des fournisseurs');
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [currentPage, pageSize]);

  // Handle search change without debounce since we're filtering locally
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Add this effect to filter suppliers based on searchTerm
  useEffect(() => {
    if (!searchTerm.trim()) {
      // If no search term, show all suppliers
      setFilteredSuppliers(suppliers);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    
    // Filter suppliers based on name, email, and matriculeFiscal
    const filtered = suppliers.filter(supplier => 
      supplier.name?.toLowerCase().includes(term) || 
      supplier.email?.toLowerCase().includes(term) || 
      supplier.matriculeFiscal?.toLowerCase().includes(term) ||
      supplier.tel?.toLowerCase().includes(term)
    );
    
    setFilteredSuppliers(filtered);
  }, [searchTerm, suppliers]);

  // Handle page size change
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Helper function for rendering page numbers with ellipsis
  const renderPageNumbers = () => {
    if (maxPage <= 1) {
      return null;
    }
    
    // Function to render a single page button
    const renderPageButton = (pageNum) => (
      <button
        key={pageNum}
        onClick={() => setCurrentPage(pageNum)}
        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
          currentPage === pageNum
            ? 'z-10 bg-[#174540]/30 border-[#174540] text-[#174540]'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        {pageNum}
      </button>
    );

    // For small number of pages, show all
    if (maxPage <= 7) {
      return [...Array(maxPage)].map((_, i) => renderPageButton(i + 1));
    }
    
    // For many pages, show with ellipsis
    const pages = [];
    
    // Always show first page
    pages.push(renderPageButton(1));
    
    // Show ellipsis if needed
    if (currentPage > 3) {
      pages.push(
        <span key="ellipsis-1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
          ...
        </span>
      );
    }
    
    // Show current page and neighbors
    const startNeighbor = Math.max(2, currentPage - 1);
    const endNeighbor = Math.min(maxPage - 1, currentPage + 1);
    
    for (let i = startNeighbor; i <= endNeighbor; i++) {
      pages.push(renderPageButton(i));
    }
    
    // Show ellipsis if needed
    if (currentPage < maxPage - 2) {
      pages.push(
        <span key="ellipsis-2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
          ...
        </span>
      );
    }
    
    // Always show last page
    pages.push(renderPageButton(maxPage));
    
    return pages;
  };

  const handleCreateSupplier = () => {
    setIsAddSupplierModalOpen(true);
  };

  const handleSubmitNewSupplier = async (supplierData) => {
    setIsSubmitting(true);
    try {
      await SupplierController.createSupplier(supplierData);
      toast.success('Fournisseur ajouté avec succès');
      setIsAddSupplierModalOpen(false);
      
      // Refresh the supplier list
      const response = await SupplierController.fetchSuppliers(currentPage, pageSize, searchTerm);
      setSuppliers(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast.error('Erreur lors de l\'ajout du fournisseur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSupplier = (supplier) => {
    setCurrentSupplier(supplier);
    setIsEditSupplierModalOpen(true);
  };

  const handleSubmitEditSupplier = async (supplierData) => {
    setIsSubmitting(true);
    try {
      // Merge the ID from currentSupplier with the updated data
      const updatedSupplier = {
        ...supplierData,
        id: currentSupplier.id
      };
      
      await SupplierController.updateSupplier(updatedSupplier);
      toast.success('Fournisseur mis à jour avec succès');
      setIsEditSupplierModalOpen(false);
      
      // Refresh the supplier list
      const response = await SupplierController.fetchSuppliers(currentPage, pageSize, searchTerm);
      setSuppliers(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);
    } catch (error) {
      console.error('Error updating supplier:', error);
      toast.error('Erreur lors de la mise à jour du fournisseur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (supplier) => {
    setSupplierToDelete(supplier);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!supplierToDelete) return;
    
    setIsSubmitting(true);
    try {
      await SupplierController.deleteSupplier(supplierToDelete.id);
      toast.success('Fournisseur supprimé avec succès');
      
      // Refresh the supplier list
      const response = await SupplierController.fetchSuppliers(currentPage, pageSize, searchTerm);
      
      // Check if we need to go back a page (if we deleted the last item on the current page)
      if (response.list.length === 0 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        setSuppliers(response.list || []);
        setTotalItems(response.total || 0);
        setMaxPage(response.maxPage || 1);
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Erreur lors de la suppression du fournisseur');
    } finally {
      setIsSubmitting(false);
      setIsDeleteModalOpen(false);
      setSupplierToDelete(null);
    }
  };

  const handleViewSupplier = async (supplier) => {
    try {
      setLoading(true);
      // Fetch the complete supplier details by ID
      const detailedSupplier = await SupplierController.getSupplierById(supplier.id);
      setViewedSupplier(detailedSupplier);
      setIsViewSupplierModalOpen(true);
    } catch (error) {
      console.error('Error fetching supplier details:', error);
      toast.error('Erreur lors du chargement des détails du fournisseur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fournisseurs</h1>
          <p className="mt-2 text-sm text-gray-700">
            Liste des fournisseurs de votre boutique.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
          disabled={!hasPermission('write')}
            type="button"
            onClick={handleCreateSupplier}
            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouveau Fournisseur
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
            placeholder="Rechercher un fournisseur par nom, email ou matricule..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 mb-2">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="text-[#174540] hover:underline"
            >
              Réessayer
            </button>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="text-center py-12 bg-white shadow overflow-hidden rounded-lg">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun fournisseur trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? "Essayez d'autres termes de recherche." : "Commencez par créer un nouveau fournisseur."}
            </p>
          </div>
        ) : (
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            {/* Desktop view */}
            <div className="hidden md:block min-w-full">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fournisseur
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Adresse
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matricule Fiscal
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                        <div className="text-sm text-gray-500">ID: {supplier.id}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{supplier.tel}</div>
                        <div className="text-sm text-gray-500">{supplier.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{supplier.adresse}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {supplier.matriculeFiscal}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button 
                            className="text-gray-600 hover:text-gray-900"
                            title="Voir les détails"
                            onClick={() => handleViewSupplier(supplier)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button 
                            disabled={!hasPermission('edit')}

                            className="text-gray-600 hover:text-gray-900  disabled:text-gray-700  disabled:cursor-not-allowed"
                            title="Éditer le fournisseur"
                            onClick={() => handleEditSupplier(supplier)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                          disabled={!hasPermission('delete')} 
                            className="text-red-600 hover:text-red-900  disabled:text-gray-700  disabled:cursor-not-allowed"
                            title="Supprimer le fournisseur"
                            onClick={() => handleDeleteClick(supplier)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile view */}
            <div className="md:hidden">
              <ul className="divide-y divide-gray-200">
                {filteredSuppliers.map((supplier) => (
                  <li key={supplier.id} className="px-4 py-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                        <div className="flex space-x-2">
                          <button 
                            className="text-gray-600 hover:text-gray-900 p-1  disabled:text-gray-700  disabled:cursor-not-allowed"
                            title="Voir les détails"
                            onClick={() => handleViewSupplier(supplier)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button 
                          disabled={!hasPermission('edit')}
                            className="text-gray-600 hover:text-gray-900 p-1  disabled:text-gray-700  disabled:cursor-not-allowed"
                            title="Éditer le fournisseur"
                            onClick={() => handleEditSupplier(supplier)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button 
                          disabled={!hasPermission('delete')}
                            className="text-red-600 hover:text-red-900 p-1  disabled:text-gray-700  disabled:cursor-not-allowed"
                            title="Supprimer le fournisseur"
                            onClick={() => handleDeleteClick(supplier)}
                          >
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-1">
                        <div className="flex items-center text-sm">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {supplier.tel}
                        </div>
                        <div className="flex items-center text-sm">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {supplier.email}
                        </div>
                        <div className="flex items-center text-sm">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {supplier.adresse}
                        </div>
                        <div className="flex items-center text-sm mt-1">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {supplier.matriculeFiscal}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredSuppliers.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          <div className="flex items-center">
            <span className="text-sm text-gray-700 mr-4">
              Affichage de {Math.min(filteredSuppliers.length, (currentPage - 1) * pageSize + 1)} à {Math.min(currentPage * pageSize, filteredSuppliers.length)} sur {filteredSuppliers.length} fournisseurs
            </span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="h-full rounded-md border border-gray-300 bg-white py-0 pl-2 pr-7 text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#174540] sm:text-sm"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              <span className="sr-only">Précédent</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Page Numbers */}
            {renderPageNumbers()}

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(maxPage, prev + 1))}
              disabled={currentPage === maxPage}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === maxPage
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              <span className="sr-only">Suivant</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      )}

      <Modal
        isOpen={isAddSupplierModalOpen}
        onClose={() => setIsAddSupplierModalOpen(false)}
        title="Ajouter un nouveau fournisseur"
        onSubmit={() => document.getElementById('addSupplierForm').requestSubmit()}
        size="lg"
        isSubmitting={isSubmitting}
        submitLabel="Créer le fournisseur"
      >
        <AddSupplier 
          formId="addSupplierForm"
          onSubmit={handleSubmitNewSupplier}
          isSubmitting={isSubmitting}
        />
      </Modal>

      <Modal
        isOpen={isEditSupplierModalOpen}
        onClose={() => setIsEditSupplierModalOpen(false)}
        title="Modifier le fournisseur"
        onSubmit={() => document.getElementById('editSupplierForm').requestSubmit()}
        size="lg"
        isSubmitting={isSubmitting}
        submitLabel="Enregistrer les modifications"
      >
        {currentSupplier && (
          <AddSupplier 
            formId="editSupplierForm"
            onSubmit={handleSubmitEditSupplier}
            isSubmitting={isSubmitting}
            initialData={currentSupplier}
          />
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        onSubmit={handleConfirmDelete}
        size="md"
        isSubmitting={isSubmitting}
        submitLabel="Supprimer"
        submitColor="red"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center text-red-500">
            <svg className="h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
              />
            </svg>
          </div>
          
          <p className="text-center text-gray-700">
            Êtes-vous sûr de vouloir supprimer le fournisseur <span className="font-semibold text-gray-900">{supplierToDelete?.name}</span> ?
          </p>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
            <div className="flex">
              <svg className="h-6 w-6 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Cette action est irréversible. Toutes les données associées à ce fournisseur seront définitivement supprimées.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewSupplierModalOpen}
        onClose={() => setIsViewSupplierModalOpen(false)}
        title="Détails du fournisseur"
        size="lg"
        hideSubmitButton={true}
      >
        {viewedSupplier ? (
          <div className="space-y-8">
            {/* Header with name and actions */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{viewedSupplier.name}</h2>
                <p className="text-sm text-gray-500">ID: {viewedSupplier.id}</p>
              </div>
              <div className="flex space-x-2">
                <button
                disabled={!hasPermission('edit')}
                  onClick={() => {
                    setIsViewSupplierModalOpen(false);
                    handleEditSupplier(viewedSupplier);
                  }}
                  className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                >
                  <svg className=" -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Modifier
                </button>
                <button
                disabled={!hasPermission('delete')}
                  onClick={() => {
                    setIsViewSupplierModalOpen(false);
                    handleDeleteClick(viewedSupplier);
                  }}
                  className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-red-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="-ml-1 mr-2 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer
                </button>
              </div>
            </div>

            {/* Contact information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 mr-2 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                Coordonnées de contact
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs font-medium text-gray-500 uppercase">Téléphone</p>
                      <p className="text-sm font-semibold text-gray-900">{viewedSupplier.tel}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs font-medium text-gray-500 uppercase">Email</p>
                      <p className="text-sm font-semibold text-gray-900">{viewedSupplier.email}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs font-medium text-gray-500 uppercase">Adresse</p>
                      <p className="text-sm font-semibold text-gray-900">{viewedSupplier.adresse}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-xs font-medium text-gray-500 uppercase">Matricule Fiscal</p>
                      <div className="mt-1">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {viewedSupplier.matriculeFiscal}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional information section - can be expanded with more data as needed */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 mr-2 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Statistiques et informations
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase">Date d'ajout</p>
                  <p className="text-sm font-semibold text-gray-900">{new Date().toLocaleDateString()}</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase">Statut</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Actif
                  </span>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase">Commandes</p>
                  <p className="text-sm font-semibold text-gray-900">0 commandes</p>
                </div>
              </div>
            </div>

            {/* Footer with actions */}
            
          </div>
        ) : (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#174540]"></div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default SuppliersPage;